/* Shopify is the sole stock, price and cart authority. Never retry a write automatically. */
(function (global) {
  'use strict';

  class CartAdapter {
    constructor({fetch: request = global.fetch.bind(global), root = '/', timeout = 15000} = {}) {
      this.request = request;
      this.root = root.endsWith('/') ? root : root + '/';
      this.timeout = timeout;
      this.busy = false;
      this.uncertain = false;
      this.cart = null;
      this.lastMutation = null;
    }

    async call(path, body) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), this.timeout);
      try {
        const response = await this.request(this.root + path, {
          method: body ? 'POST' : 'GET',
          credentials: 'same-origin',
          cache: 'no-store',
          headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
          ...(body ? {body: JSON.stringify(body)} : {}),
          signal: controller.signal
        });
        let data;
        try {
          data = await response.json();
        } catch {
          if (response.ok) throw new Error('Invalid cart response.');
        }
        if (!response.ok) {
          const error = new Error(data?.description || data?.message || (response.status === 429
            ? 'The shop is busy. Please wait a moment before trying again.'
            : 'Could not update your order. Please try again.'));
          error.definite = response.status >= 400 && response.status < 500;
          error.status = response.status;
          throw error;
        }
        return data;
      } finally {
        clearTimeout(timer);
      }
    }

    async read() {
      const cart = await this.call('cart.js');
      if (!cart || !Array.isArray(cart.items) || !Number.isInteger(cart.items_subtotal_price) ||
          !Number.isInteger(cart.total_price) || !Number.isInteger(cart.item_count)) {
        throw new Error('Invalid cart response.');
      }
      this.cart = cart;
      return cart;
    }

    async mutate(path, payload, {optional = false, context = 'product', target, groupVariantId} = {}) {
      if (this.busy) throw new Error('An order update is already running.');
      if (this.uncertain) throw new Error('Review your order on the cart page before making another change.');
      this.busy = true;
      this.lastMutation = null;
      let writeStarted = false;
      let standardEvent;
      try {
        await this.read();
        if (path === 'cart/add.js') {
          // Optional suggestions never increase an item already added in another tab.
          if (optional && payload.items.some(added => this.cart.items.some(item => item.variant_id === added.id))) return this.cart;
          const totals = new Map();
          for (const item of this.cart.items) {
            totals.set(item.variant_id, (totals.get(item.variant_id) || 0) + item.quantity);
          }
          for (const item of payload.items) {
            const quantity = (totals.get(item.id) || 0) + item.quantity;
            if (quantity > 30) throw new Error('Maximum 30 packs of each product per order. Nothing was added.');
            totals.set(item.id, quantity);
          }
        } else if (path === 'cart/update.js' && groupVariantId) {
          const matches = this.cart.items.filter(item => item.variant_id === groupVariantId && global.ToscaOffers.eligible(item));
          if (!matches.length) throw new Error('This item has changed. Review your order and try again.');
          // Resolve fresh line keys and replace the TOTAL quantity in one request.
          payload = {updates:Object.fromEntries(matches.map((item, index) => [item.key, index === 0 ? payload.quantity : 0]))};
        } else if (path === 'cart/change.js') {
          const line = this.cart.items.find(item => item.key === payload.id);
          if (!line) throw new Error('This item has changed. Review your order and try again.');
          if (line.handle === 'custom-printed-pet-cans' && payload.quantity > 0) {
            throw new Error('Custom printed cans need a quote. Remove this item and request a quote from the shop.');
          }
          if (line.selling_plan_allocation && payload.quantity > 0) {
            throw new Error('Remove this saved item and add it again from the shop to use the current price.');
          }
        }
        try {
          standardEvent = global.ToscaStandardEventsBridge?.start({
            action: path === 'cart/add.js' ? 'add' : payload.quantity === 0 ? 'remove' : 'update',
            context,
            target,
            lines: path === 'cart/add.js' ? payload.items.map(item => ({merchandiseId:item.id,quantity:item.quantity})) : path === 'cart/update.js' ? Object.entries(payload.updates).map(([id,quantity]) => ({id,quantity})) : [{id:payload.id,quantity:payload.quantity}]
          });
        } catch {}
        writeStarted = true;
        await this.call(path, payload);
        const result = await this.read();
        this.lastMutation = {path, wrote: true};
        try { standardEvent?.resolve(result); } catch {}
        return result;
      } catch (error) {
        let reconciled = false;
        if (writeStarted) {
          // Even a stock error can change Shopify's cart; always reconcile, never replay the write.
          try {
            await this.read();
            reconciled = true;
          } catch {}
          if (!error.definite || !reconciled) {
            this.uncertain = true;
            error.message = 'The connection was interrupted. Your order may have changed. Review the cart before retrying; nothing was sent twice.';
          }
        }
        try {
          if (error.definite && reconciled) standardEvent?.resolve(this.cart, {userErrors:[{code:'INVALID',message:error.message}]});
          else standardEvent?.reject(error);
        } catch {}
        throw error;
      } finally {
        this.busy = false;
      }
    }

    add(items, {optional = false, context = 'product', target, groupVariantId} = {}) {
      if (!Array.isArray(items) || !items.length) throw new Error('Choose at least one product.');
      for (const item of items) {
        if (!Number.isSafeInteger(item.id) || item.id <= 0 || !Number.isInteger(item.quantity) ||
            item.quantity < 1 || item.quantity > 30 || 'selling_plan' in item) {
          throw new Error('Check the selected product and quantity. Only current one-time purchases can be added.');
        }
      }
      return this.mutate('cart/add.js', {items: items.map(({id, quantity}) => ({id, quantity}))}, {optional,context,target});
    }

    changeGroup(variantId, quantity, {context = 'cart', target} = {}) {
      if (!global.ToscaOffers.rules[variantId] || !Number.isInteger(quantity) || quantity < 0 || quantity > 30) {
        throw new Error('Use a whole quantity from 1 to 30, or remove the item.');
      }
      return this.mutate('cart/update.js', {quantity}, {context, target, groupVariantId:variantId});
    }

    change(key, quantity, {context = 'cart', target} = {}) {
      if (typeof key !== 'string' || !key || !Number.isInteger(quantity) || quantity < 0 || quantity > 30) {
        throw new Error('Use a whole quantity from 1 to 30, or remove the item.');
      }
      return this.mutate('cart/change.js', {id: key, quantity}, {context,target});
    }
  }

  if (typeof module !== 'undefined') module.exports = {CartAdapter};
  global.ToscaCartAdapter = CartAdapter;
})(typeof window !== 'undefined' ? window : globalThis);
